// ─── Shared Google Sheets config & utilities ─────────────────────────────────
import { API_BASE } from "./api";

export const SHEET_ID  = "1eQwoa3etxf3g82jZop50lt598NJRCZ2bHRUUMQDsSOw";

// Route through the Cloudflare KV-cached worker instead of hitting the slow
// Google GViz endpoint directly. On native iOS this cuts feed load from
// ~10s to ~200ms. API_BASE is "" on web (relative) and the full domain on native.
export const SHEET_URL = `${API_BASE}/api/feed`;

export function slugify(title) {
  return (title || "")
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-");
}

export function normalize(val) {
  return (val || "").toLowerCase().trim();
}

export function formatDate(val, style = "short") {
  if (!val) return "";
  const d = new Date(Number(val) * 1000);
  if (isNaN(d)) return "";
  return style === "long"
    ? d.toLocaleDateString("en-GB", { month: "long",  day: "numeric", year: "numeric" })
    : d.toLocaleDateString("en-GB", { month: "short", day: "numeric", year: "numeric" });
}

export function parseSheetData(raw) {
  const jsonStr = raw.match(
    /google\.visualization\.Query\.setResponse\(([\s\S]*)\)/
  )?.[1];
  if (!jsonStr) return [];

  const data = JSON.parse(jsonStr);
  const cols = data.table.cols.map((c) => (c.label || "").toLowerCase().trim());

  return data.table.rows.map((row) => {
    const obj = {};
    cols.forEach((col, i) => { obj[col] = row.c[i]?.v ?? ""; });
    return obj;
  });
}

export function parseArticles(raw) {
  const cutoff = Math.floor(Date.now() / 1000) - 180 * 86400; // 180 days ago

  const rows = parseSheetData(raw)
    .filter((row) => {
      const type   = normalize(row["type"]);
      const status = normalize(row["status"]);
      if (!(type === "article" && (status === "approved" || status === "posted"))) return false;
      const ts = Number(row["date"] || 0);
      return ts === 0 || ts > cutoff;
    })
    .sort((a, b) => Number(b["date"] || 0) - Number(a["date"] || 0));

  // Client-side dedup — remove near-duplicate titles that slipped through
  // Uses word-overlap ratio: if 70%+ of significant words match, it's the same story
  const seen  = [];
  const deduped = [];

  for (const row of rows) {
    const title = normalize(row["title"] || "");
    const words = title.replace(/[^\w\s]/g, "").split(/\s+/)
      .filter(w => w.length > 3);

    const isDup = seen.some(seenWords => {
      if (!seenWords.length || !words.length) return false;
      const overlap = words.filter(w => seenWords.includes(w)).length;
      const ratio   = overlap / Math.max(words.length, seenWords.length);
      return ratio >= 0.70;
    });

    if (!isDup) {
      deduped.push(row);
      seen.push(words);
    }
  }

  return deduped;
}

export function getRelated(current, all, limit = 3) {
  const stopwords = new Set([
    "the","a","an","and","or","but","in","on","at","to","for","of","with",
    "as","by","from","is","are","was","were","be","been","has","have","had",
    "how","why","what","who","when","after","before","it","its","this","that",
  ]);

  const keywords = normalize(current["title"])
    .replace(/[^\w\s]/g, "")
    .split(/\s+/)
    .filter((w) => w.length > 3 && !stopwords.has(w));

  if (!keywords.length) return [];

  return all
    .filter((a) => a["title"] !== current["title"])
    .map((a) => {
      const haystack = normalize(a["title"] + " " + a["short caption"]);
      const score    = keywords.filter((kw) => haystack.includes(kw)).length;
      return { ...a, _score: score };
    })
    .filter((a) => a._score > 0)
    .sort((a, b) => b._score - a._score)
    .slice(0, limit);
}